/**
 * How many texts a guest will be sent, as the host sees it in Studio.
 *
 * The server never sends the host's words alone: it wraps them
 * ("Tripsby - {title}", the message, the trip link) and folds some characters.
 * Those rules change on the server, and a copy of them in the browser drifted
 * (it kept an old "always fold" rule, erased letters with no GSM equivalent,
 * and used the wrong link address). So Studio does NOT mirror them. The one
 * source of truth is OrchestratorAPI's `POST /api/groups/{id}/sms-estimate`
 * (see `groupsApi.estimateSms` and `useSmsEstimates`), which composes the text
 * exactly as the send will and answers `{ composedText, encoding, segments,
 * units, overLimit }`.
 *
 * What lives here is only what is needed while that answer is on its way, or
 * when the API has no such endpoint (an older one): a deliberately PESSIMISTIC
 * local count, always shown as "about". It folds nothing, so it can only
 * over-count, and it never decides anything: a plan is still created (or refused)
 * on the server's own validation, and a text is only called "too long" here when
 * the server said so.
 */
import type { SmsEstimateResponse } from '../types/groups';

/** The broadcast API refuses a text longer than this many segments. */
export const SMS_SEGMENT_LIMIT = 4;

/**
 * The trip link's address as the guest page lives in production. Only used for
 * the local worst case; the server's own estimate carries the real one. With
 * "Give each guest their own link" on, an invite token follows.
 */
export const TRIP_LINK_BASE = 'https://app.tripsby.ai/g/';
/** A guest's first name is not known here, so the longest plausible one stands in (the estimate may over-count, never under-count). */
export const NAME_STAND_IN = 'N'.repeat(30);
const INVITE_SUFFIX = `?invite=${'a'.repeat(43)}`;

/** GSM 03.38 basic and extension sets; anything else makes a text UCS-2. */
const GSM7_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅå' + 'Δ_ΦΓΛΩΠΨΣΘΞ' + 'ÆæßÉ' + ' !"#¤%&\'()*+,-./0123456789:;<=>?'
  + '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§' + '¿abcdefghijklmnopqrstuvwxyzäöñüà',
);
const GSM7_EXTENDED = new Set('\f^{}\\[~]|€');

export type SmsEncoding = 'GSM-7' | 'UCS-2';

export interface SmsEstimate {
  /** `server`: the API composed it. `approx`: Studio's pessimistic local count. */
  source: 'server' | 'approx';
  encoding: string;
  /** Septets (GSM-7) or UTF-16 code units (UCS-2), as the source counted them. */
  units: number;
  segments: number;
  /** Over the 4-segment limit. For `approx` this is a warning only; it never blocks anything. */
  overLimit: boolean;
  /** The text as sent to the guest it costs most, when the server said (the local count has none). */
  composedText?: string;
  /** The server's limits, when it said (else 4 texts, and the field's own 280 characters). */
  maxSegments?: number;
  maxBodyLength?: number;
  /** `no-guests`: nobody on the roster yet, so a placeholder name was measured. */
  basis?: string;
  guestsMeasured?: number;
}

/** Studio's worst-case measure of a finished text: nothing folded, so any unusual character means UCS-2. */
function measure(text: string): { encoding: SmsEncoding; units: number; segments: number } {
  let septets = 0;
  for (const ch of text) {
    if (GSM7_BASIC.has(ch)) septets += 1;
    else if (GSM7_EXTENDED.has(ch)) septets += 2;
    else {
      const units = text.length;
      return { encoding: 'UCS-2', units, segments: units === 0 ? 0 : units <= 70 ? 1 : Math.ceil(units / 67) };
    }
  }
  return { encoding: 'GSM-7', units: septets, segments: septets === 0 ? 0 : septets <= 160 ? 1 : Math.ceil(septets / 153) };
}

export interface ApproxSmsInput {
  title: string | null | undefined;
  /** The host's text as it will be sent. */
  message: string;
  slug?: string | null;
  location?: string | null;
  /** "Give each guest their own link" is on. */
  personalized: boolean;
}

/**
 * `Tripsby - {title}\n\n{message}\n\n{url}` with nothing folded: the longest the
 * text could be. Placeholders are filled whatever their case.
 */
export function approximateSms(input: ApproxSmsInput): SmsEstimate {
  const title = (input.title ?? '').trim();
  const link = `${TRIP_LINK_BASE}${(input.slug ?? '').trim() || 'your-trip'}${input.personalized ? INVITE_SUFFIX : ''}`;
  const message = input.message
    .replace(/\{url\}/gi, link)
    .replace(/\{title\}/gi, title)
    .replace(/\{location\}/gi, (input.location ?? '').trim())
    .replace(/\{name\}/gi, NAME_STAND_IN);
  const measured = measure(`Tripsby - ${title}\n\n${message}\n\n${link}`);
  return { source: 'approx', ...measured, overLimit: measured.segments > SMS_SEGMENT_LIMIT };
}

/** The API's answer as an estimate; null for anything that is not one (so the caller falls back to the local count). */
export function estimateFromServer(response: unknown): SmsEstimate | null {
  const body = response as Partial<SmsEstimateResponse> | null | undefined;
  if (!body || typeof body.segments !== 'number' || !Number.isFinite(body.segments) || typeof body.encoding !== 'string') return null;
  return {
    source: 'server',
    encoding: body.encoding,
    units: typeof body.units === 'number' ? body.units : 0,
    segments: body.segments,
    overLimit: typeof body.overLimit === 'boolean' ? body.overLimit : body.segments > (body.maxSegments ?? SMS_SEGMENT_LIMIT),
    ...(typeof body.composedText === 'string' ? { composedText: body.composedText } : {}),
    ...(typeof body.maxSegments === 'number' ? { maxSegments: body.maxSegments } : {}),
    ...(typeof body.maxBodyLength === 'number' ? { maxBodyLength: body.maxBodyLength } : {}),
    ...(typeof body.basis === 'string' ? { basis: body.basis } : {}),
    ...(typeof body.guestsMeasured === 'number' ? { guestsMeasured: body.guestsMeasured } : {}),
  };
}

const texts = (n: number) => `${n} ${n === 1 ? 'text' : 'texts'}`;

/** Plain words for the host: "About 2 texts' worth per guest" (local count) or "2 texts per guest" (the server's). */
export function describeSmsEstimate(estimate: SmsEstimate): string {
  if (estimate.source === 'server') return `${texts(estimate.segments)} per guest`;
  return `About ${estimate.segments} ${estimate.segments === 1 ? "text's" : "texts'"} worth per guest`;
}

/** The detail behind it, for a tooltip: "GSM-7 · 241 characters as sent". */
export function smsEstimateDetail(estimate: SmsEstimate): string {
  const characters = `${estimate.units} ${estimate.units === 1 ? 'character' : 'characters'}`;
  const detail = `${estimate.encoding} · ${characters} as sent, counting the trip name and link Tripsby adds.`;
  if (estimate.source !== 'server') {
    return `Roughly: ${detail} A long message is split into several texts; a character the phone network cannot send as plain text (an emoji, some accents) makes each text hold fewer characters. The exact count is checked when you review.`;
  }
  const who = estimate.basis === 'no-guests'
    ? ' Nobody is on the guest list yet, so this is measured with a placeholder name.'
    : estimate.guestsMeasured && estimate.guestsMeasured > 1
      ? ` This is the longest text among your ${estimate.guestsMeasured} guests.`
      : '';
  return `${detail}${who}`;
}

/**
 * The warning that has to appear before Review: only a server answer says the text IS too long, a local one says it may be.
 * The server also calls a body over its character cap "over the limit" even when it fits in 4 texts, and then says so.
 */
export function smsOverLimitMessage(estimate: SmsEstimate): string {
  const maxSegments = estimate.maxSegments ?? SMS_SEGMENT_LIMIT;
  if (estimate.source === 'server' && estimate.segments <= maxSegments) {
    return `This text is longer than the ${estimate.maxBodyLength ?? 280} characters a text message here can hold. Shorten it.`;
  }
  const why = estimate.encoding === 'UCS-2'
    ? ' One character the phone network cannot send as plain text (an emoji, or a letter outside its alphabet) makes every text hold fewer characters.'
    : '';
  return estimate.source === 'server'
    ? `With the trip name and link added, this is ${texts(estimate.segments)} per guest; the limit is ${maxSegments}. Shorten it.${why}`
    : `With the trip name and link added, this may run past ${maxSegments} texts per guest. Shorten it if you can; the exact count is checked when you review.${why}`;
}
